import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment
} from '@mui/material';
import SEO from '../components/SEO';
import { GitHub as GitHubIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { FaMicrosoft } from 'react-icons/fa';
import axios from 'axios';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, GithubAuthProvider, OAuthProvider, sendEmailVerification, signInWithPopup, signOut } from 'firebase/auth';
import { setStoredUser } from '../utils/userStorage';
import API_BASE from '../apiBase';
import PhoneInput from '../components/PhoneInput';
import logger from '../services/logger';
import { countryCodes } from '../utils/countryCodes';
import {
  Architecture as ArchitectureIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { Checkbox, FormControlLabel } from '@mui/material';
import { trackButtonClick, trackFormSubmit } from '../utils/gtm';

const steps = ['Basic Information', 'Address Information'];

function Signup() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [emailStatus, setEmailStatus] = useState(''); // 'available', 'taken', 'disposable', 'checking', ''
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    phone_country_code: '+1',
    password: '',
    confirmPassword: '',
    role: 'user',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    }
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validatePhone = (phone) => {
    // Only check for minimum 5 digits, allow any format
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 5;
  };

  const validateName = (name) => {
    // Name should contain only letters, spaces, hyphens, apostrophes, and periods
    // Must be at least 4 characters, can have multiple words
    const nameRegex = /^[a-zA-Z\s\-'\.]{4,50}$/;
    return nameRegex.test(name.trim());
  };

  // Check email availability and disposable-domain policy
  const checkEmailAvailability = async (email) => {
    if (!email || !validateEmail(email)) {
      setEmailStatus('');
      return;
    }

    setEmailStatus('checking');
    try {
      const response = await axios.get(`${API_BASE}/api/users/check-email?email=${encodeURIComponent(email)}`);
      if (response.data.disposable) {
        setEmailStatus('disposable');
      } else if (response.data.available) {
        setEmailStatus('available');
      } else {
        setEmailStatus('taken');
      }
    } catch (err) {
      logger.error('Email check error', err);
      setEmailStatus('');
    }
  };

  const ensureEmailAllowedForSignup = async (email) => {
    const response = await axios.get(`${API_BASE}/api/users/check-email?email=${encodeURIComponent(email)}`);
    if (response.data.disposable) {
      setEmailStatus('disposable');
      throw new Error('Disposable email addresses are not allowed. Please use a permanent work or personal email.');
    }
    if (!response.data.available) {
      setEmailStatus('taken');
      throw new Error('This email address is already registered. Please use a different email.');
    }
    setEmailStatus('available');
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;
    return strength;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      trackButtonClick('signup_step_next', 'signup_page', { step: 0 });
      // Validate basic information
      if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || !formData.phone) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate name
      if (!validateName(formData.name)) {
        setError('Please enter a valid full name (4-50 characters, letters, spaces, hyphens, apostrophes, and periods only)');
        return;
      }

      // Validate email
      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }

      // Check if email is taken or disposable
      if (emailStatus === 'taken') {
        setError('This email address is already registered. Please use a different email.');
        return;
      }
      if (emailStatus === 'disposable') {
        setError('Disposable email addresses are not allowed. Please use a permanent work or personal email.');
        return;
      }

      // Validate password
      if (!validatePassword(formData.password)) {
        setError('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Validate phone (required field)
      if (!validatePhone(formData.phone)) {
        setError('Please enter a valid phone number (at least 5 digits)');
        return;
      }

      // Validate country code (required field)
      if (!formData.phone_country_code) {
        setError('Please select a country code');
        return;
      }
    }
    setError('');
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    trackButtonClick('signup_step_back', 'signup_page', { step: activeStep });
    setError('');
    setActiveStep((prevStep) => prevStep - 1);
  };

  const bootstrapAndFetchUser = async () => {
    await axios.post(`${API_BASE}/api/users/bootstrap`);
    const { data: me } = await axios.get(`${API_BASE}/api/users/me`);
    if (!me) {
      throw new Error('User profile could not be fetched after GitHub signup.');
    }
    setStoredUser(me);
    return me;
  };

  const needsProfileCompletion = (user) => {
    if (!user) return true;
    return (
      !user.phone ||
      user.phone.trim() === '' ||
      !user.address ||
      !user.address.street1 ||
      !user.address.city ||
      !user.address.state ||
      !user.address.country
    );
  };

  const handleGitHubSignup = async () => {
    trackButtonClick('github_signup', 'signup_page');
    setLoading('github');
    setError('');
    try {
      const provider = new GithubAuthProvider();
      provider.addScope('read:user');
      provider.addScope('user:email');

      await signInWithPopup(auth, provider);
      const profile = await bootstrapAndFetchUser();

      trackFormSubmit('github_signup', 'signup_page', true);
      if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'github_signup' });
      if (needsProfileCompletion(profile)) {
        navigate('/complete-profile', { state: { user: profile } });
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      logger.error('GitHub signup failed', err);
      trackFormSubmit('github_signup', 'signup_page', false, { error: err?.message });
      if (err?.code === 'auth/popup-closed-by-user') {
        setError('GitHub signup was closed before completion. Please try again.');
      } else {
        setError('Failed to sign up with GitHub: ' + (err?.message || 'Please try again.'));
      }
      try { await auth.signOut(); } catch {}
    } finally {
      setLoading(null);
    }
  };


  const handleAzureDevOpsSignup = async () => {
    trackButtonClick('microsoft_signup', 'signup_page');
    setLoading('microsoft');
    setError('');
    try {
      const provider = new OAuthProvider('microsoft.com');
      const tenantId = process.env.REACT_APP_MS_TENANT_ID || process.env.REACT_APP_AZURE_TENANT_ID;
      if (tenantId) {
        provider.setCustomParameters({ tenant: tenantId });
      }
      provider.addScope('openid');
      provider.addScope('profile');
      provider.addScope('email');
      provider.addScope('User.Read');

      await signInWithPopup(auth, provider);
      const profile = await bootstrapAndFetchUser();

      trackFormSubmit('microsoft_signup', 'signup_page', true);
      if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'microsoft_signup' });
      if (needsProfileCompletion(profile)) {
        navigate('/complete-profile', { state: { user: profile } });
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      logger.error('Microsoft signup failed', err);
      trackFormSubmit('microsoft_signup', 'signup_page', false, { error: err?.message });
      if (err?.code === 'auth/popup-closed-by-user') {
        setError('Microsoft signup was closed before completion. Please try again.');
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        setError('This email is already linked to another login method. Sign in with that method first, then link Microsoft from Profile Settings.');
      } else {
        setError('Failed to sign up with Microsoft: ' + (err?.message || 'Please try again.'));
      }
      try { await auth.signOut(); } catch {}
    } finally {
      setLoading(null);
    }
  };

  const handleEmailSignup = async (e) => {
    if (e) e.preventDefault();
    if (!consentChecked) {
      setError('You must agree to the Compliance Policy to continue');
      return;
    }
    if (!termsConsent) {
      setError('You must agree to the Terms of Service to continue');
      return;
    }
    if (!privacyConsent) {
      setError('You must agree to the Privacy Policy to continue');
      return;
    }

    // Basic validation for step 0 fields
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!validatePassword(formData.password)) {
      setError('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate phone (required field)
    if (!validatePhone(formData.phone)) {
      setError('Please enter a valid phone number (at least 5 digits)');
      return;
    }

    // Validate country code (required field)
    if (!formData.phone_country_code) {
      setError('Please select a country code');
      return;
    }

    // Validation for step 1 fields (address information)
    if (!formData.address.street1) {
      setError('Please enter your street address');
      return;
    }

    if (!formData.address.city) {
      setError('Please enter your city');
      return;
    }

    if (!formData.address.state) {
      setError('Please enter your state/province');
      return;
    }

    if (!formData.address.postal_code) {
      setError('Please enter your postal code');
      return;
    }

    if (!formData.address.country) {
      setError('Please enter your country');
      return;
    }

    setLoading('email');
    setError('');

    try {
      await ensureEmailAllowedForSignup(formData.email);

      // 1) Create Firebase Auth user (Identity Platform)
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);

      // Fire-and-forget verification email (non-blocking)
      try {
        await sendEmailVerification(userCredential.user, {
          url: `${window.location.origin}/auth/action`,
          handleCodeInApp: true,
        });
      } catch (verificationErr) {
        logger.error('Failed to send verification email', verificationErr);
      }

      // 2) Bootstrap backend user (idempotent) and fetch profile (fail fast)
      await axios.post(`${API_BASE}/api/users/bootstrap`);
      const { data: me } = await axios.get(`${API_BASE}/api/users/me`);
      if (!me?.id) {
        throw new Error('User profile missing after bootstrap.');
      }

      // 3) Update backend user with signup details if we have an id
      if (me?.id) {
        await axios.put(`${API_BASE}/api/users/${me.id}`, {
          name: formData.name,
          company_name: formData.company_name || '',
          phone: formData.phone || '',
          role: formData.role || 'user',
          address: formData.address || {
            street1: '',
            street2: '',
            city: '',
            state: '',
            postal_code: '',
            country: ''
          },
          terms_of_service_consent: termsConsent,
          privacy_policy_consent: privacyConsent
        });
      }

      // 4) Store fresh user cache
      try {
        const { data } = await axios.get(`${API_BASE}/api/users/me`);
        if (data) setStoredUser(data);
      } catch (_) {}

      // 5) Upsert compliance policy immediately after signup
      try {
        await axios.put(`${API_BASE}/api/compliance/policy`, {
          data_region: formData.address?.country || '',
          retention_basis: 'payment basis',
          lawful_basis: 'contract',
          consent_required: true,
          consent_source_default: 'user_signup_checkbox',
          purposes: ['code review automation', 'fix issues'],
          pii_categories: ['email', 'username'],
          dsr_contact: formData.email,
          anonymize_on_export: true,
        }, {});
      } catch (_) {}

      // Sign out to enforce verification on next login and redirect with notice
      try { await signOut(auth); } catch (_) {}
      trackFormSubmit('email_signup', 'signup_page', true);
      if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'email_signup' });
      navigate('/login', {
        state: {
          verificationNotice: true,
          verificationEmail: formData.email
        }
      });
    } catch (err) {
      logger.error('Signup error', err);
      trackFormSubmit('email_signup', 'signup_page', false, { error: err?.message });

      // Handle specific error cases
      if (err?.response?.status === 403 && err?.response?.data?.details) {
        setError(err.response.data.details);
      } else if (err?.response?.status === 409) {
        setError('A user with this email address already exists. Please use a different email or try logging in.');
      } else if (err?.response?.data?.error) {
        setError(err.response.data.error + ': ' + (err.response.data.details || ''));
      } else if (err?.response?.data?.details) {
        setError(err.response.data.details);
      } else {
        setError('Signup failed: ' + err.message);
      }
    }
    setLoading(null);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
            <Box sx={{ width: '100%' }}>
            <TextField
              required
              fullWidth
              id="full-name"
              label="Full Name"
              value={formData.name}
              aria-describedby={formData.name && !validateName(formData.name) ? 'name-error' : undefined}
              autoComplete="name"
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Jane Doe"
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
            />
            {formData.name && !validateName(formData.name) && (
              <Typography id="name-error" variant="caption" sx={{
                color: '#f44336',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ❌ Name must be 4-50 characters and can include letters, spaces, hyphens, and apostrophes.
              </Typography>
            )}

            <TextField
              required
              fullWidth
              id="email-address"
              label="Email Address"
              value={formData.email}
              onChange={(e) => {
                handleInputChange('email', e.target.value);
                const timeoutId = setTimeout(() => {
                  checkEmailAvailability(e.target.value);
                }, 500);
                return () => clearTimeout(timeoutId);
              }}
              placeholder="you@company.com"
              type="email"
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
              InputProps={{
                endAdornment: emailStatus === 'checking' ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} sx={{ color: '#000' }} />
                  </InputAdornment>
                ) : emailStatus === 'available' ? (
                  <InputAdornment position="end">
                    <Box sx={{ color: '#4caf50', fontSize: 16 }}>✓</Box>
                  </InputAdornment>
                ) : emailStatus === 'taken' || emailStatus === 'disposable' ? (
                  <InputAdornment position="end">
                    <Box sx={{ color: '#f44336', fontSize: 16 }}>✗</Box>
                  </InputAdornment>
                ) : null
              }}
            />
            {formData.email && !validateEmail(formData.email) && (
              <Typography variant="caption" sx={{
                color: '#f44336',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ❌ Please enter a valid email address
              </Typography>
            )}
            {emailStatus === 'taken' && (
              <Typography variant="caption" sx={{
                color: '#f44336',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ❌ This email is already registered
              </Typography>
            )}
            {emailStatus === 'disposable' && (
              <Typography variant="caption" sx={{
                color: '#f44336',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ❌ Disposable email addresses are not allowed
              </Typography>
            )}
            {emailStatus === 'available' && (
              <Typography variant="caption" sx={{
                color: '#4caf50',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ✅ This email is available
              </Typography>
            )}

            <TextField
              required
              fullWidth
              id="company-name"
              label="Company Name (Optional)"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="Enter your company name"
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
            />

            <PhoneInput
              required={true}
              id="phone-number"
              label="Phone Number"
              placeholder="Enter your phone number"
              value={formData.phone}
              countryCode={formData.phone_country_code}
              onChange={(value) => handleInputChange('phone', value)}
              onCountryCodeChange={(value) => handleInputChange('phone_country_code', value)}
              disabled={!!loading}
              error={!!(formData.phone && !validatePhone(formData.phone))}
              helperText={formData.phone && !validatePhone(formData.phone) ? 'Please enter at least 5 digits' : ''}
              sx={{ mb: 1 }}
            />

            <TextField
              required
              fullWidth
              id="password"
              label="Password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: formData.password ?
                      (validatePassword(formData.password) ? '#4caf50' : '#f44336') : '#e0e0e0'
                  },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {formData.password && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1, fontSize: 12 }}>
                  Password Strength: {getPasswordStrength(formData.password)}/5
                </Typography>
                {/* Password Strength Bar */}
                <Box sx={{ width: '100%', mb: 1 }}>
                  <Box sx={{
                    width: '100%',
                    height: 4,
                    bgcolor: '#e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      width: `${(getPasswordStrength(formData.password) / 5) * 100}%`,
                      height: '100%',
                      bgcolor: getPasswordStrength(formData.password) <= 2 ? '#f44336' :
                        getPasswordStrength(formData.password) <= 3 ? '#ff9800' :
                          getPasswordStrength(formData.password) <= 4 ? '#2196f3' : '#4caf50',
                      transition: 'all 0.3s ease'
                    }} />
                  </Box>
                </Box>
                {/* Password Requirements */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {[
                    { label: '8+ chars', met: formData.password.length >= 8 },
                    { label: 'Lowercase', met: /[a-z]/.test(formData.password) },
                    { label: 'Uppercase', met: /[A-Z]/.test(formData.password) },
                    { label: 'Number', met: /\d/.test(formData.password) },
                    { label: 'Special', met: /[@$!%*?&]/.test(formData.password) }
                  ].map((req, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '10px',
                        bgcolor: req.met ? '#4caf50' : '#e0e0e0',
                        color: req.met ? '#fff' : '#666',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25
                      }}
                    >
                      {req.met ? '✓' : '○'} {req.label}
                    </Box>
                  ))}
                </Box>
                {!validatePassword(formData.password) && (
                  <Typography variant="caption" sx={{
                    color: '#f44336',
                    fontWeight: 'bold',
                    fontSize: 12
                  }}>
                    ❌ Password must contain at least 8 characters, including uppercase, lowercase, number, and special character
                  </Typography>
                )}
                {validatePassword(formData.password) && (
                  <Typography variant="caption" sx={{
                    color: '#4caf50',
                    fontWeight: 'bold',
                    fontSize: 12
                  }}>
                    ✅ Password meets all requirements
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              required
              fullWidth
              id="confirm-password"
              label="Confirm Password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: formData.confirmPassword ?
                      (formData.password === formData.confirmPassword && formData.password ? '#4caf50' : '#f44336') : '#e0e0e0'
                  },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <Typography variant="caption" sx={{
                color: '#f44336',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ❌ Passwords do not match
              </Typography>
            )}
            {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password && (
              <Typography variant="caption" sx={{
                color: '#4caf50',
                fontWeight: 'bold',
                display: 'block',
                mb: 1,
                fontSize: 12
              }}>
                ✅ Passwords match
              </Typography>
            )}

            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontSize: 14 }}>
              Role
            </Typography>
            <TextField
              select
              fullWidth
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="developer">Developer</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
            </TextField>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ width: '100%' }}>
            <TextField
              required
              id="street-address-1"
              label="Street Address 1"
              fullWidth
              autoComplete="address-line1"
              value={formData.address.street1}
              onChange={(e) => handleInputChange('address.street1', e.target.value)}
              placeholder="123 Main Street"
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
            />

            <TextField
              id="street-address-2"
              label="Street Address 2 (Optional)"
              fullWidth
              autoComplete="address-line2"
              value={formData.address.street2}
              onChange={(e) => handleInputChange('address.street2', e.target.value)}
              placeholder="Apartment, suite, etc."
              disabled={!!loading}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#bdbdbd' },
                  '&.Mui-focused fieldset': { borderColor: '#000' },
                  borderRadius: 1,
                  bgcolor: '#fff',
                  fontSize: 14,
                  height: '50px'
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.7,
                  fontSize: 14,
                  height: '50px'
                }
              }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  required
                  id="city"
                  label="City"
                  fullWidth
                  autoComplete="address-level2"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  placeholder="New York"
                  disabled={!!loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#bdbdbd' },
                      '&.Mui-focused fieldset': { borderColor: '#000' },
                      borderRadius: 1,
                      bgcolor: '#fff',
                      fontSize: 14,
                      height: '50px'
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.7,
                      fontSize: 14,
                      height: '50px'
                    }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  required
                  id="state"
                  label="State/Province"
                  fullWidth
                  autoComplete="address-level1"
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  placeholder="NY"
                  disabled={!!loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#bdbdbd' },
                      '&.Mui-focused fieldset': { borderColor: '#000' },
                      borderRadius: 1,
                      bgcolor: '#fff',
                      fontSize: 14,
                      height: '50px'
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.7,
                      fontSize: 14,
                      height: '50px'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  required
                  id="postal-code"
                  label="Postal Code"
                  fullWidth
                  autoComplete="postal-code"
                  value={formData.address.postal_code}
                  onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                  placeholder="10001"
                  disabled={!!loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#bdbdbd' },
                      '&.Mui-focused fieldset': { borderColor: '#000' },
                      borderRadius: 1,
                      bgcolor: '#fff',
                      fontSize: 14,
                      height: '50px'
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.7,
                      fontSize: 14,
                      height: '50px'
                    }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  required
                  id="country"
                  label="Country"
                  fullWidth
                  value={formData.address.country}
                  autoComplete="country-name"
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                  placeholder="United States"
                  disabled={!!loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#bdbdbd' },
                      '&.Mui-focused fieldset': { borderColor: '#000' },
                      borderRadius: 1,
                      bgcolor: '#fff',
                      fontSize: 14,
                      height: '50px'
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.7,
                      fontSize: 14,
                      height: '50px'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} sx={{ '&.Mui-checked': { color: '#000' } }} />}
                label={<Typography variant="body2">I agree to the <Link component={RouterLink} to="/compliance-policy" target="_blank" sx={{ color: '#000', fontWeight: 500 }}>Compliance Policy</Link></Typography>}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={termsConsent} onChange={(e) => setTermsConsent(e.target.checked)} sx={{ '&.Mui-checked': { color: '#000' } }} />}
                label={<Typography variant="body2">I agree to the <Link component={RouterLink} to="/terms-of-use" target="_blank" sx={{ color: '#000', fontWeight: 500 }}>Terms of Service</Link></Typography>}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={privacyConsent} onChange={(e) => setPrivacyConsent(e.target.checked)} sx={{ '&.Mui-checked': { color: '#000' } }} />}
                label={<Typography variant="body2">I agree to the <Link component={RouterLink} to="/privacy-policy" target="_blank" sx={{ color: '#000', fontWeight: 500 }}>Privacy Policy</Link></Typography>}
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        title="Sign Up - Sniffr.ai | Start Your Free Trial"
        description="Create your Sniffr.ai account and start automating code reviews with AI. Free trial available. Supports GitHub, GitLab, and Azure DevOps integration."
        keywords="Sniffr.ai signup, code review sign up, AI code review free trial, GitHub integration, GitLab integration, developer tools signup"
        url="/signup"
      />
      <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center",
      background: '#fff'
    }}>
      <Card sx={{
        width: '100%',
        maxWidth: 1100,
        height: 900,
        display: 'flex',
        borderRadius: 2,
        boxShadow: '0 20px 40px rgba(40, 34, 34, 0.1)',
        overflow: 'hidden',
        padding: '30px',
        border: '1px solid #e0e0e0'
      }}>
        {/* Left Panel - Gradient Background */}
        <Box
          sx={{
            flex: 1,
            background: 'linear-gradient(135deg,hsl(16 100% 66%),hsl(195 100% 50%),hsl(270 100% 70%))',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderRadius: 2,
            p: 4,
            position: 'relative',
          }}
        >
          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', mb: 4, cursor: 'pointer', width: 'fit-content' }}
          >
            <Box
              component="img"
              sx={{ height: 40, mr: 2, borderRadius: 5, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
              alt="Logo"
              src="/assets/logo.png"
            />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, '&:hover': { opacity: 0.85 }, transition: 'opacity 0.2s' }}>
              Sniffr.ai
            </Typography>
          </Box>
          <Box>
            {/* Welcome Text */}
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 800,
                fontSize: 30,
                mb: 2,
                lineHeight: 2
              }}
            >
              Welcome to Sniffr.ai
            </Typography>

            {/* Feature List */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'hsl(16 100% 66%)',
                    mr: 2,
                    opacity: 0.8,
                  }}
                />
                <ArchitectureIcon sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  Think like an architect
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'hsl(16 100% 66%)',
                    mr: 2,
                    opacity: 0.8
                  }}
                />
                <AssignmentTurnedInIcon sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  Validate like a BA
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'hsl(16 100% 66%)',
                    mr: 2,
                    opacity: 0.8
                  }}
                />
                <ScienceIcon sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                  Test like senior QA
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'left' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: 2
            }}>
              <Box sx={{ width: 8, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
              <Box sx={{ width: 30, height: 6, bgcolor: '#fff', borderRadius: 2 }} />
            </Box>
          </Box>
        </Box>

        {/* Right Panel - Signup Form */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'top',
            alignItems: 'center',
            p: { xs: 2, sm: 4 },
            // padding: '0px 30px',
            maxWidth: { xs: '100%', md: '50%' },
            minHeight: { xs: '100vh', md: 'auto' }
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 500 }}>
            {/* Top Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box
                onClick={() => navigate('/')}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <Box
                  component="img"
                  sx={{ height: 24, mr: 1, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  alt="Logo"
                  src="/assets/logo.png"
                />
                <Typography variant="h6" sx={{ color: '#000', fontWeight: 600, fontSize: 18, '&:hover': { color: '#374151' }, transition: 'color 0.2s' }}>
                  Sniffr.ai
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.1, backgroundColor: 'hsl(0 0% 96%)', padding: '0.25rem', borderRadius: 2 }}>
                <RouterLink to="/login" style={{ textDecoration: 'none' }}>
                  <Button
                    onClick={() => trackButtonClick('sign_in_header', 'signup_page')}
                    sx={{
                      color: '#666',
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 500,
                      px: 3,
                    }}
                  >
                    Sign In
                  </Button>
                </RouterLink>
                <Button
                  sx={{
                    color: '#000',
                    bgcolor: '#fff',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            </Box>


            {/* Stepper */}
            <Box sx={{ width: '100%', mb: 3 }}>
              <Stepper activeStep={activeStep} sx={{
                '& .MuiStepLabel-root .Mui-completed': { color: '#000' },
                '& .MuiStepLabel-root .Mui-active': { color: '#000' },
                '& .MuiStepConnector-line': { borderColor: '#e0e0e0' }
              }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel sx={{
                      color: '#666',
                      '& .MuiStepLabel-label': {
                        fontSize: 14,
                        fontWeight: 500
                      }
                    }}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Form Content */}
            <Box autoComplete="off" sx={{ width: '100%' }}>
              {renderStepContent(activeStep)}

              {error && (
                <Alert severity="error" sx={{ mb: 1, fontSize: 14 }}>
                  {error}
                </Alert>
              )}

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                {activeStep !== 0 && (
                  <Button
                    onClick={handleBack}
                    sx={{
                      color: '#666',
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 500
                    }}
                  >
                    Back
                  </Button>
                )}
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleEmailSignup}
                    disabled={!!loading}
                    sx={{
                      bgcolor: '#000',
                      color: '#fff',
                      py: 1,
                      px: 3,
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#333' },
                      '&:disabled': { bgcolor: '#ccc' }
                    }}
                  >
                    {loading === 'email' ? <CircularProgress size={20} color="inherit" /> : 'Create Account'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      bgcolor: '#000',
                      color: '#fff',
                      py: 1,
                      px: 3,
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#333' }
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
            {/* bottom Navigation */}
            <Box>
              <Divider sx={{ my: 1, color: '#999' }}>OR</Divider>
              {/* OAuth Buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={loading === 'github' ? <CircularProgress size={16} /> : <GitHubIcon />}
                  onClick={handleGitHubSignup}
                  disabled={!!loading}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 1,
                    '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' }
                  }}
                >
                  GitHub
                </Button>
                <Button
                  variant="outlined"
                  startIcon={loading === 'microsoft' ? <CircularProgress size={16} /> : <FaMicrosoft />}
                  onClick={handleAzureDevOpsSignup}
                  disabled={!!loading}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderColor: '#e0e0e0',
                    color: '#333',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 1,
                    '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' }
                  }}
                >
                  Microsoft
                </Button>
              </Box>

              <Box sx={{ width: '100%', textAlign: 'center', mt: 3, mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 400 }}>
                  Already have an account?{' '}
                  <Link 
                    href="/login" 
                    onClick={(e) => {
                      e.preventDefault();
                      trackButtonClick('sign_in_bottom', 'signup_page');
                      navigate('/login');
                    }}
                    underline="hover" 
                    sx={{ color: '#000', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                  >
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
    </>
  );
}

export default Signup;